import { FormControl, FormErrorMessage, useBoolean } from "@chakra-ui/react";
import * as Sentry from "@sentry/react";
import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { useCallback, useRef, useState } from "react";

import { extractErrorMessage } from "@equidefi/portals";
import { useApi } from "@equidefi/portals/hooks/useApi";

import { ProcessingFeeSummary } from "@/pages/workflow/payment/ProcessingFeeSummary";

import StripeClient from "../clients/StripeClient";
import { CONFIG } from "../constants/config";
import { useOfferingAnalytics } from "../hooks/useOfferingAnalytics";
import Spinner from "./spinner";
import { WorkflowButton } from "./workflow/WorkflowButton";

const baseUrl = new URL(CONFIG.URL);

// Breadcrumbs rather than logs: they cost nothing when the payment succeeds and
// reconstruct the whole attempt on the Sentry error when it doesn't.
const trace = (message, data) => {
  Sentry.addBreadcrumb({ category: "stripe.payment", level: "info", message, data });
};

const StripePaymentForm = ({
  investment,
  paymentIntentId,
  setPaymentMethod,
  returnPath = `${investment?.id}/payment/processing`,
  offering,
  skipFeeToggle = false,
  deferredIntent = false,
  amountInCents = null,
}) => {
  const analytics = useOfferingAnalytics(offering, investment);

  const returnUrl = new URL(returnPath, baseUrl);

  const [isValid, setIsValid] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [methodType, setMethodType] = useState(null);
  const [isLoading, { on: startLoading, off: stopLoading }] = useBoolean();

  const stripeApi = useApi(StripeClient);

  // `isLoading` only disables the button on the next render, which is too late to
  // stop a second click landing in the same tick and creating a second intent.
  const isSubmitting = useRef(false);

  const handleOnReady = () => {
    setIsReady(true);
    trace("PaymentElement ready", {
      investmentId: investment?.id,
      deferredIntent,
      amountInCents,
    });
  };

  /** @param {import("@stripe/stripe-js").StripeElementChangeEvent} data */
  const handleOnChange = (data) => {
    setPaymentMethod(data.value.type);
    setMethodType(data.value.type);
    if (!deferredIntent && !skipFeeToggle) {
      stripeApi.togglePaymentIntentType(investment?.id, paymentIntentId, data.value.type);
    }
    setIsValid(data.complete);
  };

  const stripe = useStripe();
  const elements = useElements();

  const [errorMessage, setErrorMessage] = useState(null);

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      if (!stripe || !elements) return;
      if (isSubmitting.current) return;
      isSubmitting.current = true;

      analytics.track("payment", {
        amount: investment.amount,
        type: "stripe",
      });

      startLoading();
      setErrorMessage(null);

      try {
        if (!deferredIntent) {
          const { error } = await stripe.confirmPayment({
            elements,
            confirmParams: {
              return_url: returnUrl.toString(),
            },
          });

          if (error) setErrorMessage(error.message);
          return;
        }

        const { error: submitError } = await elements.submit();
        if (submitError) {
          trace("elements.submit() rejected", { code: submitError.code, type: submitError.type });
          setErrorMessage(submitError.message);
          return;
        }

        let intent;
        try {
          intent = await stripeApi.createPaymentIntent(investment?.id, {
            payment_method_type: methodType ?? "card",
          });
        } catch (e) {
          trace("PaymentIntent creation failed", { paymentMethodType: methodType ?? "card" });
          Sentry.captureException(e);
          setErrorMessage(extractErrorMessage(e, "We couldn't start your payment. Please try again."));
          return;
        }

        trace("PaymentIntent created", {
          paymentIntentId: intent.id,
          paymentMethodType: methodType ?? "card",
          serverAmount: intent.amount,
          mountedAmount: amountInCents,
        });

        // The server is authoritative on price. Confirming while Elements still
        // holds a different amount charges the card and *then* fails, so the
        // element has to be told before confirm rather than after.
        if (intent.amount !== amountInCents) {
          Sentry.captureMessage("Stripe Elements amount diverged from the PaymentIntent", {
            level: "warning",
            extra: {
              paymentIntentId: intent.id,
              paymentMethodType: methodType ?? "card",
              serverAmount: intent.amount,
              mountedAmount: amountInCents,
            },
          });
          elements.update({ amount: intent.amount });
        }

        const { error } = await stripe.confirmPayment({
          elements,
          clientSecret: intent.clientSecret,
          confirmParams: {
            return_url: returnUrl.toString(),
          },
        });

        if (error) {
          trace("confirmPayment rejected", {
            paymentIntentId: intent.id,
            code: error.code,
            type: error.type,
            decline_code: error.decline_code,
          });
          setErrorMessage(error.message);
        }
      } finally {
        stopLoading();
        isSubmitting.current = false;
      }
    },
    [
      analytics.track,
      stripe,
      elements,
      returnUrl,
      startLoading,
      stopLoading,
      investment,
      deferredIntent,
      methodType,
      amountInCents,
      stripeApi,
    ],
  );

  return (
    <form onSubmit={handleSubmit}>
      {!isReady && <Spinner show />}
      <PaymentElement className="fvMask" onReady={handleOnReady} onChange={handleOnChange} />

      <FormControl isInvalid={!!errorMessage}>
        {errorMessage && <FormErrorMessage>{errorMessage}</FormErrorMessage>}
      </FormControl>

      <ProcessingFeeSummary investment={investment} offering={offering} paymentType={methodType ?? "card"} />

      <WorkflowButton type="submit" mt="4" isLoading={isLoading} isDisabled={!isValid}>
        Pay
      </WorkflowButton>
    </form>
  );
};

export default StripePaymentForm;
